import useBreakpoint from '../../../../assets/frontend/js/hooks/breakpoint'
import FilterPanel from './FilterPanel'
import List from './List'
import LocationsMap from './LocationsMap'
import classNames from 'classnames'
import { useAppSelector, useAppStore } from '../store/hooks'
import { filterActions } from '../store/filterSlice'
import { useHits, useSearchBox, useStats } from 'react-instantsearch'
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import type { LocationHit } from '../types'
import useScrollDirection from '../../../../assets/frontend/js/hooks/useScrollDirection'
import ActionButtons from '../../../../assets/frontend/js/components/ActionButtons'
import useIsFullscreen from '../../../../assets/frontend/js/hooks/useIsFullscreen'
import Sticky from '../../../../assets/frontend/js/components/Sticky'
import LayoutToggle from './LayoutToggle'
import { DataProvider } from './Context'
import { prepareItems } from '../utils/data'
export type View = 'list' | 'map'

export default function SearchUI() {
  const store = useAppStore()
  const geoLocation = useAppSelector((state) => state.config.geoLocation)
  const { items } = useHits<LocationHit>()

  const itemsPerPage = useAppSelector((state) => state.config.postsPerPage)
  const [displayItemCount, setDisplayItemCount] = useState(itemsPerPage)

  const { query } = useSearchBox()
  const { nbHits } = useStats()

  const timeoutRef = useRef<NodeJS.Timeout>()
  const lastTrackedQuery = useRef<string>('')

  const trackSearch = useCallback(() => {
    if (
      query &&
      query.trim().length > 0 &&
      query !== lastTrackedQuery.current
    ) {
      lastTrackedQuery.current = query
      if (typeof window !== 'undefined' && (window as any).dataLayer) {
        ;(window as any).dataLayer.push({
          event: 'locations_search',
          search_query: query.trim(),
          search_results_count: nbHits,
          search_no_results: nbHits === 0
        })
      }
    }
  }, [query, nbHits])

  useEffect(() => {
    if (query && query.trim().length > 0) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(trackSearch, 1000)
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [query, trackSearch])

  const showMore = () => {
    setDisplayItemCount((current) => current + itemsPerPage)
  }

  const preparedItems = useMemo(
    () =>
      [...prepareItems(items, geoLocation)].sort(
        (a, b) => a.distance - b.distance
      ),
    [items, geoLocation]
  )

  const listItems = preparedItems.slice(0, displayItemCount)

  const isLastPage = listItems.length < displayItemCount

  const isDesktop = useBreakpoint('lg')
  const isTablet = useBreakpoint('md')
  const displayMap = useAppSelector((state) => state.filter.displayMap)

  const onViewChange = (view: View) => {
    store.dispatch(filterActions.setDisplayMap(view === 'map'))
  }

  /* biome-ignore lint/correctness/useExhaustiveDependencies: only react to displayMap */
  useEffect(() => {
    if (wrapperRef.current && !isDesktop && displayMap) {
      const wrapperElement = wrapperRef.current
      setTimeout(() => {
        const scrollOffset = 100;
        const top = wrapperElement.getBoundingClientRect().top + window.scrollY - scrollOffset;
        window.scrollTo({ top, behavior: 'instant' });
      }, 0)
    }
  }, [displayMap])

  const wrapperRef = useRef<HTMLDivElement>(null)
  const isWrapperVisible = useIsFullscreen(wrapperRef)
  const scrollDirection = useScrollDirection()

  const collapseUi =
    !isTablet && scrollDirection === 'down' && !displayMap && isWrapperVisible

  const isSticky = !isTablet

  const filterModalIdRef = useRef<string>(useId())

  return (
    <DataProvider items={preparedItems}>
      <div className="rounded overflow-clip" ref={wrapperRef}>
        <Sticky isSticky={isSticky}>
          <FilterPanel
            view={displayMap ? 'map' : 'list'}
            changeView={onViewChange}
            collapsed={collapseUi}
            modalId={filterModalIdRef.current}
          />
        </Sticky>
        <div className="my-2">
          {isDesktop ? (
            <div className="row">
              <div
                className={classNames(
                  'col-12',
                  displayMap && 'col-lg-5 col-xl-6'
                )}
              >
                <List
                  items={listItems}
                  isLastPage={isLastPage}
                  showMore={showMore}
                />
              </div>
              {displayMap && (
                <div className="col-12 col-lg-7 col-xl-6">
                  <LocationsMap />
                </div>
              )}
            </div>
          ) : displayMap ? (
            <div>
              <LocationsMap />
            </div>
          ) : (
            <List
              items={listItems}
              isLastPage={isLastPage}
              showMore={showMore}
            />
          )}
        </div>
        <Sticky alignment="bottom" isSticky={isSticky}>
          <ActionButtons
            collapsed={collapseUi}
            modalId={filterModalIdRef.current}
            layoutToggle={
              <LayoutToggle
                variant="mobile"
                value={displayMap ? 'map' : 'list'}
                onChange={onViewChange}
              />
            }
          />
        </Sticky>
      </div>
    </DataProvider>
  )
}
